import React, {useCallback, useEffect, useMemo, useState} from "react";
import {addMonths} from "date-fns";
import {useDispatch, useSelector} from "react-redux";
import {toKSTISOString} from "../../components/commonmethod/CommonMethod";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {getDate, setDate} from "../../redux/slice/dateSlice";
import CalendaModal from "../static/rbd/chart/CalendaModal";

const DATE_KEY = "업무달력";

const WEEKDAYS = [
    {label: "일", className: "text-[#ef4444]"},
    {label: "월", className: "text-[#374151]"},
    {label: "화", className: "text-[#374151]"},
    {label: "수", className: "text-[#374151]"},
    {label: "목", className: "text-[#374151]"},
    {label: "금", className: "text-[#374151]"},
    {label: "토", className: "text-[#3b82f6]"},
];

const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const parseIsoDate = (iso) => {
    if (!iso) return null;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const buildMonthCells = (viewMonth) => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells = [];

    for (let i = 0; i < firstWeekday; i++) {
        const day = daysInPrevMonth - firstWeekday + i + 1;
        cells.push({date: new Date(year, month - 1, day), inMonth: false});
    }

    for (let day = 1; day <= daysInMonth; day++) {
        cells.push({date: new Date(year, month, day), inMonth: true});
    }

    let nextDay = 1;
    while (cells.length % 7 !== 0) {
        cells.push({date: new Date(year, month + 1, nextDay), inMonth: false});
        nextDay += 1;
    }

    return cells;
};

function ChevronButton({direction, onClick, ariaLabel}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={ariaLabel}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-[18px] leading-none text-[#9ca3af] transition-colors hover:text-[#374151]"
        >
            {direction === "prev" ? "‹" : "›"}
        </button>
    );
}

function TaskCalendarContent({data}) {
    const dispatch = useDispatch();
    const calendarSearch = useSelector((state) => getDate(state, DATE_KEY));
    const today = useMemo(() => new Date(), []);

    const [viewMonth, setViewMonth] = useState(() => {
        const clicked = parseIsoDate(calendarSearch?.clickedDate);
        return startOfMonth(clicked ?? today);
    });
    const [selectedDate, setSelectedDate] = useState(() => {
        return parseIsoDate(calendarSearch?.clickedDate) ?? today;
    });
    const [modalOpen, setModalOpen] = useState(false);

    const dotSet = useMemo(() => new Set(data?.dots ?? []), [data?.dots]);
    const delayDotSet = useMemo(() => new Set(data?.delayDots ?? []), [data?.delayDots]);
    const monthCells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
    const monthLabel = `${viewMonth.getMonth() + 1}월`;

    useEffect(() => {
        const clicked = parseIsoDate(calendarSearch?.clickedDate);
        if (clicked) {
            setSelectedDate(clicked);
            setViewMonth(startOfMonth(clicked));
        }
    }, [calendarSearch?.clickedDate]);

    const publishMonthRange = useCallback(
        (monthDate) => {
            dispatch(
                setDate({
                    keys: DATE_KEY,
                    startDate: toKSTISOString(addMonths(monthDate, -1)),
                    endDate: toKSTISOString(addMonths(monthDate, 1)),
                })
            );
        },
        [dispatch]
    );

    const changeMonth = (delta) => {
        const nextMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1);
        setViewMonth(nextMonth);
        publishMonthRange(nextMonth);
    };

    const handleDayClick = (date) => {
        setSelectedDate(date);
        dispatch(
            setDate({
                keys: DATE_KEY,
                clickedDate: toKSTISOString(date),
            })
        );
        if (data?.data?.length || data?.delayData?.length) {
            setModalOpen(true);
        }
    };

    return (
        <div className="task-calendar relative flex h-full min-h-0 flex-col bg-white">
            <h3 className="shrink-0 text-[21px] font-bold leading-none text-[#111827]">
                업무 캘린더
            </h3>

            <div className="mt-1 flex shrink-0 items-center justify-between border-b border-[#e5e7eb]">
                <ChevronButton
                    direction="prev"
                    ariaLabel="이전 달"
                    onClick={() => changeMonth(-1)}
                />
                <span className="text-[15px] font-semibold text-[#6b7c93]">{monthLabel}</span>
                <ChevronButton
                    direction="next"
                    ariaLabel="다음 달"
                    onClick={() => changeMonth(1)}
                />
            </div>

            <div className="mt-2 grid shrink-0 grid-cols-7 gap-y-1 text-center">
                {WEEKDAYS.map((day) => (
                    <div
                        key={day.label}
                        className={`py-1 text-[11px] font-bold tracking-wide ${day.className}`}
                    >
                        {day.label}
                    </div>
                ))}
            </div>

            <div className="mt-1 grid min-h-0 flex-1 auto-rows-fr grid-cols-7 gap-y-0.5 overflow-hidden text-center">
                {monthCells.map((cell) => {
                    const key = toDateKey(cell.date);
                    const selected = isSameDay(cell.date, selectedDate);
                    const isToday = isSameDay(cell.date, today);
                    const highlighted = selected || (isToday && cell.inMonth);
                    const hasDot = dotSet.has(key);
                    const hasDelay = delayDotSet.has(key);

                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleDayClick(cell.date)}
                            className="relative flex min-h-0 min-w-0 items-center justify-center rounded py-0.5 hover:bg-[#f8fafc]"
                        >
                            {hasDelay && (
                                <span
                                    className="pointer-events-none absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[#b23609]"
                                    aria-hidden
                                />
                            )}
                            <span
                                className={[
                                    "inline-flex aspect-square h-full min-h-[22px] max-h-7 items-center justify-center rounded-full px-1 text-[12px] leading-none",
                                    highlighted
                                        ? "bg-[#3b82f6] font-semibold text-white"
                                        : cell.inMonth
                                          ? "text-[#111827]"
                                          : "text-[#c4c4c4]",
                                ].join(" ")}
                            >
                                {cell.date.getDate()}
                            </span>
                            {hasDot && (
                                <span
                                    className="pointer-events-none absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[#22c55e]"
                                    aria-hidden
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {modalOpen && (data?.data || data?.delayData) && (
                <CalendaModal
                    data={data.data}
                    delayList={data.delayData}
                    modalhandler={setModalOpen}
                />
            )}
        </div>
    );
}

export function TaskCalendar({data, loading = false}) {
    if (!data && loading) {
        return (
            <div className="task-calendar relative flex h-full min-h-0 flex-col bg-white">
                <h3 className="shrink-0 text-[16px] font-bold leading-none text-[#111827]">
                    업무 캘린더
                </h3>
                <ChartLoadingOverlay />
            </div>
        );
    }

    return (
        <div className="relative flex h-full min-h-0 flex-col">
            {loading && <ChartLoadingOverlay />}
            <TaskCalendarContent data={data} />
        </div>
    );
}

export default TaskCalendar;
